import { useState } from "react";
import { useForm } from "react-hook-form"

import useYumPaymentSDK from "@/account/hooks/useYumPaymentSDK";
import formattedPrice from "@/common/formattedPrice";
import { classifyGiftCardType } from "@/checkout/helpers/classifyGiftCardType";
import { GiftCardTypes } from "@/domain/cart/types";
import { THIRD_PARTY_CARD_ENTERED_MESSAGE } from "@/shared/pages/gift-cards/YumBalanceChecker/constants";

type GiftCardFormInput = {
    cardNumber: string;
    pin: string;
}

type YumGiftCardBalanceCheckProps = {
    yumToken: string;
}

export const useYumGiftCardBalanceCheck = ({ yumToken }: YumGiftCardBalanceCheckProps) => {
    const {
        formState: { errors },
        register,
        handleSubmit,
        getValues,
        setError,
        clearErrors,
        setValue
    } = useForm<GiftCardFormInput>();
    const [, { checkGiftCardBalance }] = useYumPaymentSDK({ yumToken });

    const [isLoading, setIsLoading] = useState(false);
    const [balance, setBalance] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const onCardNumberChanged = (value: string): void => {
        const type = classifyGiftCardType({ cardNumber: value });
        if (type === GiftCardTypes.ThirdParty) {
            setError("cardNumber", {
                type: "manual",
                message: THIRD_PARTY_CARD_ENTERED_MESSAGE
            });
        } else {
            clearErrors("cardNumber");
            setValue("cardNumber", value);
        }
    };

    const onPinNumberChanged = (value: string): void => {
        setValue("pin", value);
    }

    const handleBalanceCheck = async ({ cardNumber, pin }: GiftCardFormInput) => {
        setErrorMessage('');
        setIsLoading(true);
        setBalance('');

        try {
            checkGiftCardBalance({ 
                cardNumber,
                pin,
                onSuccess: ({ amount }) => { 
                    setBalance(formattedPrice(amount))
                    setIsLoading(false);
                },
                onError: (error) => { 
                    setErrorMessage(error.message);
                    setIsLoading(false);
                }
            });
        } catch (error) {
            setIsLoading(false);
        }
    }

    return [
        {
            isLoading,
            balance,
            errorMessage,
            errors
        },
        {
            register,
            handleSubmit,
            handleBalanceCheck,
            getValues,
            onCardNumberChanged,
            onPinNumberChanged
        }
    ]
}